import React, { useState, useEffect } from 'react';
import { Button, Table, message, Space, Dropdown, Menu, Tooltip } from 'antd';
import { useLazyQuery, useMutation } from '@apollo/client';
import moment from 'moment';

import { FETCH_ADMIN_MAIL_JOBS } from 'graphql/queries/adminQueries';
import { RESEND_ADMIN_MAIL_JOB } from 'graphql/mutations/adminMutations';
import FilterIcon from 'components/Icons/FilterIcon';

import './SignatureMails.scss';

const limit = 30;

const SignatureMails = ({ tab = 'mails' }) => {
  const [mailJobs, setMailJobs] = useState(0);
  const [mailJobsType, setMailJobsType] = useState('');
  const [cursorPaging, setCursorPaging] = useState(null);
  const [isFetching, setIsFetching] = useState(true);

  const emailType = tab === 'reminder-emails' ? 'reminder' : tab === 'make_pdf' ? 'make_pdf' : null;

  const processFetchedData = (data, shouldConcat = false) => {
    if (data?.fetchAdminMailJobs) {
      const { mailJobs: newMailJobs, pagingMetadata } = data.fetchAdminMailJobs;

      setMailJobs([...(shouldConcat ? mailJobs : []), ...newMailJobs]);
      setCursorPaging(pagingMetadata);
    }

    setIsFetching(false);
  };

  const [fetchAdminMailJobs, { refetch, fetchMore }] = useLazyQuery(FETCH_ADMIN_MAIL_JOBS, {
    fetchPolicy: 'network-only',
    onCompleted: (data) => processFetchedData(data),
  });

  useEffect(() => {
    setIsFetching(true);
    fetchAdminMailJobs({
      variables: { type: mailJobsType, limit, emailType },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mailJobsType]);

  const loadMore = async () => {
    setIsFetching(true);
    const { data } = await fetchMore({
      variables: {
        type: mailJobsType,
        emailType,
        limit,
        cursorPaging: cursorPaging?.cursor,
      },
    });
    processFetchedData(data, true);
  };

  const refetchMailJobs = async () => {
    setIsFetching(true);

    const { data } = await refetch({
      type: mailJobsType,
      limit,
      emailType,
    });
    processFetchedData(data);
  };

  const [resendAdminMailJob] = useMutation(RESEND_ADMIN_MAIL_JOB, {
    onCompleted: () => {
      message.success('Job mail added to queue successfully');
      refetchMailJobs();
    },
    onError: () => {
      message.error(`Failed to resend mail`);
    },
  });

  const jobAction = async (data) => {
    resendAdminMailJob({
      variables: {
        jobId: data._id,
      },
    });
  };

  const filterMailJobs = ({ key }) => {
    setMailJobsType(key);
  };

  const mailJobsColumn = [
    {
      title: 'Created At',
      dataIndex: 'time',
      key: 'time',
      render: (data) => <>{moment(data).format('DD/MM/YYYY')}</>,
    },
    {
      title: 'User ID',
      dataIndex: 'userid',
      key: 'userid',
    },
    {
      title: 'Proposal ID',
      dataIndex: 'propid',
      key: 'propid',
    },
    ...(emailType === 'reminder' || emailType === 'make_pdf'
      ? [
          {
            title: 'Type',
            dataIndex: 'type',
            key: 'type',
            render: (type) => (
              <>
                {emailType === 'reminder'
                  ? type === '1st_reminder_to_prospect'
                    ? '1st email reminder'
                    : type === '2nd_reminder_to_prospect'
                    ? '2nd email reminder'
                    : type === 'published_proposal_to_prospect'
                    ? 'Published proposal to prospect'
                    : type
                  : emailType === 'make_pdf'
                  ? 'Make PDF'
                  : ''}
              </>
            ),
          },
          {
            title: 'Status',
            dataIndex: 'done',
            key: 'done',
            render: (status) => (
              <>{status === true ? 'Completed' : status === false ? 'Not started' : status}</>
            ),
          },
          {
            title: 'Actions',
            key: 'actions',
            render: (data) => (
              <Button onClick={() => jobAction(data)}>
                {data?.done === true
                  ? 'Resend'
                  : data?.done === false && emailType === 'reminder'
                  ? 'Resend'
                  : data?.done === 'inprogress'
                  ? 'Resend Inprogress Job'
                  : 'Resend'}
              </Button>
            ),
          },
        ]
      : [
          {
            title: 'Status',
            dataIndex: 'done',
            key: 'done',
            render: (status) => (
              <>
                {status === true
                  ? 'Completed'
                  : status === 'inprogress'
                  ? 'In Progress'
                  : 'Not started'}
              </>
            ),
          },
          {
            title: 'Actions',
            key: 'actions',
            render: (data) => (
              <Button onClick={() => jobAction(data)}>
                {data?.done === true
                  ? 'Resend'
                  : data?.done === 'inprogress'
                  ? 'Resend Inprogress Job'
                  : 'Resend'}
              </Button>
            ),
          },
        ]),
  ];

  const menu = (
    <Menu onClick={filterMailJobs} defaultSelectedKeys={['all']}>
      <Menu.Item key="all">All</Menu.Item>
      <Menu.Item key="sent">Sent</Menu.Item>
      <Menu.Item key="not_sent">Not Sent</Menu.Item>
      <Menu.Item key="inprogress">In Progress</Menu.Item>
    </Menu>
  );

  return (
    <div className="admin-wrapper">
      <div className="admin-table-wrapper">
        <div className="table-action">
          <h3>
            {emailType === 'reminder'
              ? 'Reminder Mail Jobs'
              : emailType === 'make_pdf'
              ? 'Make PDF Jobs'
              : 'Signature Mail Jobs'}
          </h3>
          <div>
            <Space>
              <Dropdown overlay={menu}>
                <Tooltip title="Status Filter">
                  <FilterIcon />
                </Tooltip>
              </Dropdown>
            </Space>
          </div>
        </div>

        <Table
          loading={isFetching}
          dataSource={mailJobs}
          columns={mailJobsColumn}
          rowKey={'_id'}
          pagination={false}
          footer={() => (
            <div>
              {cursorPaging?.hasNext && (
                <Button
                  loading={isFetching}
                  disabled={isFetching}
                  className="medium-btn white-btn"
                  onClick={loadMore}>
                  Load more
                </Button>
              )}
            </div>
          )}
        />
      </div>
    </div>
  );
};

export default SignatureMails;
